import { SecShape, type SecondaryDef } from '../../sim/secondary';

/** S09 — ORAGE. La seule arme du catalogue qu'on renonce à tirer parce qu'il
 *  n'y a pas ASSEZ de cibles.
 *
 *  ── Le besoin ────────────────────────────────────────────────────────────
 *  Toutes les autres armes s'améliorent avec le nombre d'ennemis, mais aucune
 *  n'en DÉPEND : la Lance reste bonne sur une cible isolée, le Faisceau aussi,
 *  les Mines finissent par attraper quelque chose. Aucune ne pose au joueur la
 *  question « y en a-t-il assez ? », et donc aucune ne récompense de laisser un
 *  groupe se former au lieu de le grignoter.
 *
 *  L'Orage ne fait qu'un contact sur un ennemi seul — 55 dégâts, la pire valeur
 *  du catalogue, moins qu'une seconde de tir primaire. Sur quatre ennemis
 *  groupés, il en fait quatre. La décision qu'il crée n'est donc pas « quand
 *  tirer » mais « quand ne PAS tirer », ce qu'aucune autre arme ne demande. Un
 *  joueur qui déclenche l'Orage sur un traînard a gaspillé sa recharge, et il le
 *  sait à l'instant où il la presse.
 *
 *  ── Pourquoi il ignore les angles et pas les distances ───────────────────
 *  Un arc ne se dirige pas : il saute vers ce qui est le plus proche, y compris
 *  derrière lui, mais seulement à 270 u. C'est l'inverse exact du Traqueur, qui
 *  voit à 520 u dans un cône de 55°. Les deux armes partagent la même mécanique
 *  de moteur — acquérir une cible, tourner vers elle — et ce seul échange de
 *  bornes suffit à en faire deux armes opposées : l'une demande d'orienter son
 *  tir, l'autre de s'être approché. La géométrie du joueur remplace la géométrie
 *  du tir.
 *
 *  ── Ce que ça implique pour le joueur, et c'est le sujet ─────────────────
 *  Il faut ENTRER dans le groupe. Le scellement de tir (§6.5) éteint les
 *  émetteurs sous 150 u, donc s'approcher n'est pas suicidaire — c'est même le
 *  geste que le jeu récompense le plus mal ailleurs, faute d'une arme qui le
 *  paye. L'Orage est cette arme. Elle transforme la zone la plus inconfortable
 *  du terrain en position de tir.
 *
 *  ── L'équilibrage ────────────────────────────────────────────────────────
 *  49 DPS nominaux (24 % de la primaire), et le nominal suppose ici les quatre
 *  contacts, donc trois ennemis à moins de 270 u les uns des autres au moment
 *  du saut. La réalisation moyenne est très inférieure — c'est le prix d'une
 *  arme qui, quand la situation se présente, nettoie une nuée en un clic.
 *
 *  ── ⚠️ Nom de fichier ────────────────────────────────────────────────────
 *  Trié après `lance.def.ts`, comme tout ce qui a été ajouté depuis. Voir
 *  l'avertissement complet dans `trainee.def.ts`. */
const orage: SecondaryDef = {
  id: 'orage-electrique',
  name: 'Orage électrique',
  shape: SecShape.Arc,
  // 4,5 s. Courte, parce que l'arme rate souvent par la faute du terrain et non
  // du joueur : une recharge longue sur une arme conditionnelle punit deux fois
  // la même mauvaise situation.
  cooldown: 4.5,
  shots: 1,
  shotEvery: 0,
  count: 1,
  spacing: 0,
  spreadDeg: 0,
  // 1 400 u/s. Le trait doit atteindre son premier corps presque instantanément
  // — c'est un arc, pas un projectile — tout en restant sous le plafond de
  // 1 500 u/s qui garantit 0,8 s de traversée d'écran.
  speed: 1400,
  radius: 16,
  damage: 55,
  // Le contact initial plus trois sauts.
  hits: 4,
  // 1 600 °/s : le trait se réoriente en trois ticks. Un arc qui virerait
  // lentement dessinerait des courbes, ce qui est le vocabulaire du missile —
  // et le joueur lirait alors l'arme comme un traqueur médiocre plutôt que
  // comme une décharge.
  turnDeg: 1600,
  text: '55 dégâts × 4 sauts entre ennemis proches — 4,5 s',
};

export default orage;
